// src/Login.jsx
import { useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";
import { auth, googleProvider } from "./firebase";

// ── Design tokens (matches Dashboard.jsx) ─────────────────────────────────────
const T = {
  bg:          "#0f1117",
  surface:     "#161b27",
  raised:      "#1a2035",
  border:      "rgba(255,255,255,0.07)",
  borderFocus: "rgba(99,102,241,0.5)",
  text:        "#e8eaf0",
  textMuted:   "#6b7280",
  textDim:     "#9ca3af",
  accent:      "#6366f1",
  accentLight: "rgba(99,102,241,0.15)",
  danger:      "#ef4444",
  dangerLight: "rgba(239,68,68,0.12)",
  radius:      "14px",
  radiusSm:    "9px",
};

const BENEFITS = [
  { icon: "📋", text: "Track every Canvas deadline in one place" },
  { icon: "⏱", text: "See your weekly workload at a glance" },
  { icon: "🔔", text: "Get reminders before assignments are due" },
];

// ── Responsive hook ────────────────────────────────────────────────────────────
function useWindowWidth() {
  const [width, setWidth] = useState(
    () => (typeof window !== "undefined" ? window.innerWidth : 1280)
  );
  useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);
  return width;
}

export default function Login() {
  const [mode, setMode]         = useState("login"); // "login" | "signup"
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const windowWidth = useWindowWidth();
  const isMobile    = windowWidth < 768;

  // ── Auth handlers — unchanged ──────────────────────────────────────────────
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      console.log("GOOGLE LOGIN RESULT:", result.user?.uid, result.user?.email);
    } catch (err) {
      console.error("GOOGLE LOGIN ERROR:", err);
      setError(err.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (field) => ({
    width: "100%",
    background: T.raised,
    border: `1px solid ${focusedField === field ? T.borderFocus : T.border}`,
    borderRadius: T.radiusSm,
    color: T.text,
    fontSize: 14,
    padding: "12px 14px",
    outline: "none",
    fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
    boxShadow: focusedField === field ? "0 0 0 3px rgba(99,102,241,0.12)" : "none",
    transition: "border-color 0.15s, box-shadow 0.15s",
    boxSizing: "border-box",
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { overflow-x: hidden; max-width: 100vw; }
        input::placeholder { color: #374151; }
        input:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 100px #1a2035 inset !important;
          -webkit-text-fill-color: #e8eaf0 !important;
        }
        @keyframes dq-spin { to { transform: rotate(360deg); } }
        .dq-login-btn-primary:hover:not(:disabled) {
          box-shadow: 0 6px 20px rgba(99,102,241,0.4) !important;
          transform: translateY(-1px);
        }
        .dq-login-btn-google:hover:not(:disabled) {
          background: rgba(255,255,255,0.08) !important;
          border-color: rgba(255,255,255,0.16) !important;
        }
        .dq-mode-toggle:hover { color: #a5b4fc !important; }

        /* Mobile: stack columns */
        @media (max-width: 767px) {
          .dq-login-shell {
            flex-direction: column !important;
            border-radius: 12px !important;
          }
          .dq-login-auth {
            flex: none !important;
            width: 100% !important;
            padding: 28px 20px 24px !important;
            border-right: none !important;
            border-bottom: 1px solid rgba(255,255,255,0.07) !important;
          }
          .dq-login-value {
            padding: 24px 20px !important;
          }
          .dq-login-value h2 {
            font-size: 18px !important;
          }
          .dq-login-value p {
            font-size: 13px !important;
            margin-bottom: 20px !important;
          }
          .dq-benefit-text {
            font-size: 13px !important;
            padding-top: 5px !important;
          }
          .dq-social-proof {
            margin-top: 20px !important;
            padding-top: 16px !important;
          }
        }

        /* Small mobile: tighten further */
        @media (max-width: 400px) {
          .dq-login-auth {
            padding: 22px 16px 20px !important;
          }
          .dq-login-value {
            padding: 20px 16px !important;
          }
        }
      `}</style>

      {/* ── Page wrapper ─────────────────────────────────────────────────── */}
      <div
        style={{
          minHeight: "100vh",
          background: T.bg,
          display: "flex",
          alignItems: isMobile ? "flex-start" : "center",
          justifyContent: "center",
          padding: isMobile ? "16px 12px 32px" : "24px 16px",
          fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
        }}
      >
        {/* ── Two-column shell — stacks on mobile ──────────────────────── */}
        <div
          className="dq-login-shell"
          style={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            maxWidth: 860,
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: T.radius,
            overflow: "hidden",
            boxShadow: isMobile
              ? "0 8px 32px rgba(0,0,0,0.4)"
              : "0 24px 64px rgba(0,0,0,0.5)",
          }}
        >
          {/* ── Auth card ──────────────────────────────────────────────── */}
          <div
            className="dq-login-auth"
            style={{
              flex: "0 0 420px",
              padding: "40px 36px",
              display: "flex",
              flexDirection: "column",
              gap: 0,
              borderRight: `1px solid ${T.border}`,
            }}
          >

            {/* Headline */}
            <h1 style={{ fontSize: isMobile ? 20 : 22, fontWeight: 800, color: T.text, letterSpacing: "-0.5px", marginBottom: 5 }}>
              {mode === "login" ? "Welcome back" : "Create your account"}
            </h1>
            <p style={{ fontSize: 13, color: T.textMuted, marginBottom: isMobile ? 20 : 26, lineHeight: 1.5 }}>
              {mode === "login"
                ? "Log in to track your assignments and stay on top of deadlines."
                : "Get started free — takes under a minute."}
            </p>

            {/* Google button */}
            <button
              type="button"
              className="dq-login-btn-google"
              onClick={handleGoogle}
              disabled={loading}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                width: "100%",
                padding: "11px 16px",
                background: "rgba(255,255,255,0.04)",
                border: `1px solid ${T.border}`,
                borderRadius: T.radiusSm,
                color: T.text,
                fontSize: 13,
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
                transition: "all 0.15s",
                marginBottom: 16,
                opacity: loading ? 0.6 : 1,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            {/* Divider */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1, height: 1, background: T.border }} />
              <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 500 }}>or</span>
              <div style={{ flex: 1, height: 1, background: T.border }} />
            </div>

            {/* Email/password form */}
            <form onSubmit={handleEmailSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                  Email address
                </label>
                <input
                  type="email"
                  placeholder="you@university.edu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocusedField("email")}
                  onBlur={() => setFocusedField(null)}
                  required
                  style={inputStyle("email")}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                  Password
                </label>
                <input
                  type="password"
                  placeholder={mode === "signup" ? "At least 6 characters" : "Your password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedField("password")}
                  onBlur={() => setFocusedField(null)}
                  required
                  style={inputStyle("password")}
                />
              </div>

              {/* Error */}
              {error && (
                <div style={{ background: T.dangerLight, border: "1px solid rgba(239,68,68,0.2)", borderRadius: T.radiusSm, padding: "9px 13px", fontSize: 12, color: "#fca5a5", lineHeight: 1.4 }}>
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                className="dq-login-btn-primary"
                disabled={loading}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  width: "100%",
                  padding: "12px 16px",
                  marginTop: 4,
                  background: loading ? "rgba(99,102,241,0.5)" : "linear-gradient(135deg, #6366f1, #818cf8)",
                  border: "none",
                  borderRadius: T.radiusSm,
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: loading ? "not-allowed" : "pointer",
                  fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
                  boxShadow: loading ? "none" : "0 4px 14px rgba(99,102,241,0.35)",
                  transition: "all 0.15s",
                  letterSpacing: "-0.1px",
                }}
              >
                {loading ? (
                  <>
                    <span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.35)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "dq-spin 0.7s linear infinite" }} />
                    {mode === "login" ? "Logging in…" : "Creating account…"}
                  </>
                ) : mode === "login" ? "Log in" : "Create account"}
              </button>
            </form>

            {/* Mode toggle */}
            <p style={{ marginTop: 18, fontSize: 13, color: T.textMuted, textAlign: "center" }}>
              {mode === "login" ? "Don't have an account? " : "Already have an account? "}
              <button
                type="button"
                className="dq-mode-toggle"
                onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); }}
                style={{ background: "none", border: "none", color: "#818cf8", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif', padding: 0, transition: "color 0.15s" }}
              >
                {mode === "login" ? "Sign up free" : "Log in"}
              </button>
            </p>
          </div>

          {/* ── Value prop panel ───────────────────────────────────────── */}
          <div
            className="dq-login-value"
            style={{
              flex: 1,
              background: "rgba(99,102,241,0.04)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              padding: "48px 40px",
            }}
          >
            {/* Tagline badge */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: T.accentLight,
                border: "1px solid rgba(99,102,241,0.25)",
                borderRadius: 6,
                padding: "4px 10px",
                fontSize: 11,
                fontWeight: 700,
                color: "#a5b4fc",
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                marginBottom: 16,
                width: "fit-content",
              }}
            >
              ✦ Free for students
            </div>

            <h2
              style={{
                fontSize: isMobile ? 18 : 24,
                fontWeight: 800,
                color: T.text,
                letterSpacing: "-0.5px",
                lineHeight: 1.25,
                marginBottom: 10,
              }}
            >
              Never miss a Canvas deadline again.
            </h2>

            <p
              style={{
                fontSize: isMobile ? 13 : 14,
                color: T.textMuted,
                lineHeight: 1.6,
                marginBottom: isMobile ? 20 : 28,
              }}
            >
              Connect Canvas once and DueQueue keeps everything organized — deadlines, workload estimates, and reminders all in one place.
            </p>

            {/* Benefits */}
            <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 10 : 14 }}>
              {BENEFITS.map((b, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 8,
                      background: T.accentLight,
                      border: "1px solid rgba(99,102,241,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      flexShrink: 0,
                    }}
                  >
                    {b.icon}
                  </div>
                  <div
                    className="dq-benefit-text"
                    style={{ fontSize: 13, fontWeight: 500, color: T.textDim, lineHeight: 1.5, paddingTop: 6 }}
                  >
                    {b.text}
                  </div>
                </div>
              ))}
            </div>

            {/* Social proof */}
            <div
              className="dq-social-proof"
              style={{
                marginTop: 32,
                paddingTop: 20,
                borderTop: `1px solid ${T.border}`,
                fontSize: 12,
                color: T.textMuted,
                lineHeight: 1.5,
              }}
            >
              <span style={{ color: T.text, fontWeight: 600 }}>Built for students</span>{" "}
              who want less chaos and more clarity in their academic week.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}